import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks';
import { useLogout } from '../../../features/auth/hooks';

/**
 * ProfileDropdown
 * What: User menu with navigation items (no pages wired yet except redirects)
 * Why: Matches today's header structure and IA
 * Where: shared/components/layout/ProfileDropdown.js
 *
 * Enhancement: Adds a right-side arrow (ChevronDown) to indicate dropdown functionality.
 * This is a standard UX pattern for dropdowns, improving discoverability and clarity.
 */
export const ProfileDropdown = () => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { data: user } = useAuth({ enabled: false });
  const { mutateAsync: logout, isPending: logoutLoading } = useLogout();
  const location = useLocation();

  // Close dropdown if user clicks outside
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-200"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
          {user?.fullName ? (
            <span className="text-sm font-semibold text-white">
              {user.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          ) : (
            <User className="h-4 w-4 text-white" />
          )}
        </div>
        <span className="hidden md:block text-base font-medium text-gray-700">
          {user?.username || 'User'}
        </span>
        {/* Right-side arrow for dropdown indication */}
        <ChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

        {open && (
         <div
           className="absolute right-0 mt-2 w-56 rounded-lg border border-gray-200 bg-white shadow-lg z-[9999] overflow-hidden"
           role="menu"
         >
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900">
              {user?.fullName || 'User'}
            </p>
            <p className="text-sm text-gray-500 truncate">
              {user?.email}
            </p>
          </div>
          
          <div className="py-1">
            <Link
              to="/account"
              className={`block px-4 py-2 text-base ${
                location.pathname === '/account'
                  ? 'bg-primary-50 text-primary-800'
                  : 'text-gray-700 hover:bg-primary-50 hover:text-primary-800'
              }`}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Account
            </Link>
            <Link
              to="/settings"
              className={`block px-4 py-2 text-base ${
                location.pathname === '/settings'
                  ? 'bg-primary-50 text-primary-800'
                  : 'text-gray-700 hover:bg-primary-50 hover:text-primary-800'
              }`}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              Settings
            </Link>
          </div>
          <div className="border-t border-gray-200" />
            <button
             className="w-full text-left px-4 py-2 text-base text-error-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
             role="menuitem"
             disabled={logoutLoading}
             onClick={async () => {
               // Don't close dropdown during logout - let user see the "Signing Out..." text
               await logout();
             }}
           >
             {logoutLoading ? 'Signing Out...' : 'Sign Out'}
           </button>
        </div>
      )}
    </div>
  );
};
